import { Badge, Group } from '@mantine/core';
import {
  IconArchive,
  IconCircleCheck,
  IconCircleDashed,
  IconClock,
  IconEye,
  IconWorld,
} from '@tabler/icons-react';
import type { ProgrammeStatus, VideoStatus } from '@/api/types';
import { STATUS_LABEL } from '@/auth/permissions';

// Status always pairs an icon and a word with the colour (design guidelines: hue never alone).
const STATUS: Record<ProgrammeStatus, { color: string; icon: typeof IconEye }> = {
  draft: { color: 'gray', icon: IconCircleDashed },
  in_review: { color: 'orange', icon: IconEye },
  approved: { color: 'sky', icon: IconCircleCheck },
  published: { color: 'leaf', icon: IconWorld },
  archived: { color: 'gray', icon: IconArchive },
};

export function StatusBadge({
  status,
  scheduled,
}: {
  status: ProgrammeStatus;
  scheduled?: boolean;
}) {
  const s = STATUS[status];
  const Icon = s.icon;
  return (
    <Group gap={4} wrap="nowrap">
      <Badge
        color={s.color}
        variant="light"
        leftSection={<Icon size={12} aria-hidden />}
        td={status === 'archived' ? 'line-through' : undefined}
      >
        {STATUS_LABEL[status]}
      </Badge>
      {scheduled && (
        <Badge color="lilac" variant="outline" leftSection={<IconClock size={12} aria-hidden />}>
          Scheduled
        </Badge>
      )}
    </Group>
  );
}

const VIDEO: Record<VideoStatus, { color: string; label: string }> = {
  uploading: { color: 'sky', label: 'Uploading' },
  processing: { color: 'orange', label: 'Processing' },
  ready: { color: 'leaf', label: 'Ready' },
  errored: { color: 'coral', label: 'Failed' },
};

export function VideoStatusBadge({ status }: { status: VideoStatus }) {
  const v = VIDEO[status];
  return (
    <Badge
      color={v.color}
      variant="light"
      leftSection={
        status === 'ready' ? (
          <IconCircleCheck size={12} aria-hidden />
        ) : (
          <IconClock size={12} aria-hidden />
        )
      }
    >
      {v.label}
    </Badge>
  );
}
